import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/api';
import type { Database, Json } from '@/types/database.generated';

export type PatientRow = Database['public']['Tables']['patients']['Row'];
export type PatientStatus = Database['public']['Enums']['patient_status'];

export interface PatientListItem {
  id: string;
  pat_number: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  status: PatientStatus;
  has_cpr: boolean;
  tags: string[];
  created_at: string;
  updated_at: string;
  clinic_id: string;
  clinic_name?: string;
}

export interface CreatePatientInput {
  clinic_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  cpr?: string;
  address?: {
    street?: string;
    city?: string;
    zip_code?: string;
    country?: string;
  };
  notes?: string;
  tags?: string[];
  marketing_consent_email?: boolean;
  marketing_consent_sms?: boolean;
  [key: string]: unknown;
}

export interface UpdatePatientInput {
  patient_id: string;
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string | null;
  cpr?: string | null;
  address?: {
    street?: string;
    city?: string;
    zip_code?: string;
    country?: string;
  };
  notes?: string | null;
  tags?: string[];
  status?: PatientStatus;
  marketing_consent_email?: boolean;
  marketing_consent_sms?: boolean;
  [key: string]: unknown;
}

export interface PatientMutationOutput {
  id: string;
  clinic_id: string;
  pat_number: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  has_cpr: boolean;
  address: Record<string, unknown>;
  notes: string | null;
  tags: string[];
  status: PatientStatus;
  marketing_consent_email: boolean;
  marketing_consent_sms: boolean;
  updated_at: string;
}

export interface PatientListFilters {
  search?: string;
  status?: PatientStatus;
}

export const patientsListQueryKey = (
  clinicIds: string[] | string | null,
  filters: PatientListFilters,
) => ['patients', 'list', clinicIds, filters] as const;

export const patientDetailQueryKey = (patientId: string | null | undefined) =>
  ['patients', 'detail', patientId] as const;

export function usePatientsList(
  clinicIds: string[] | string | null | undefined,
  filters: PatientListFilters,
) {
  // Normalize to array
  const clinicIdsArray = useMemo(() => {
    if (!clinicIds) return [];
    return Array.isArray(clinicIds) ? clinicIds : [clinicIds];
  }, [clinicIds]);

  const enabled = clinicIdsArray.length > 0;

  return useQuery({
    queryKey: patientsListQueryKey(clinicIdsArray, filters),
    enabled,
    queryFn: async (): Promise<PatientListItem[]> => {
      if (clinicIdsArray.length === 0) return [];

      let query = supabase
        .from('patients')
        .select(
          'id, pat_number, first_name, last_name, email, phone, status, encrypted_cpr, tags, created_at, updated_at, clinic_id, clinics!inner(name)',
        )
        .in('clinic_id', clinicIdsArray)
        .eq('status', filters.status ?? 'active')
        .order('last_name', { ascending: true })
        .order('first_name', { ascending: true })
        .limit(500);

      const search = filters.search?.trim();
      if (search) {
        if (search.length <= 2) {
          // ilike fallback for very short queries (1-2 chars) where FTS is noisy
          const pattern = `%${search}%`;
          query = query.or(
            `first_name.ilike.${pattern},last_name.ilike.${pattern},email.ilike.${pattern},phone.ilike.${pattern},pat_number.ilike.${pattern}`,
          );
        } else {
          const tsQuery = search
            .split(/\s+/)
            .filter((t) => t.length > 0)
            .map((t) => `${t}:*`)
            .join(' & ');
          if (tsQuery) {
            query = query.textSearch('search_tsv', tsQuery, { config: 'simple' });
          }
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data ?? []).map(
        (row): PatientListItem => ({
          id: row.id,
          pat_number: row.pat_number,
          first_name: row.first_name,
          last_name: row.last_name,
          email: row.email,
          phone: row.phone,
          status: row.status,
          has_cpr: row.encrypted_cpr !== null && row.encrypted_cpr !== undefined,
          tags: row.tags ?? [],
          created_at: row.created_at,
          updated_at: row.updated_at,
          clinic_id: row.clinic_id,
          clinic_name: (row.clinics as any)?.name,
        }),
      );
    },
    staleTime: 30_000,
  });
}

export function usePatient(patientId: string | null | undefined) {
  return useQuery({
    queryKey: patientDetailQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientRow & { has_cpr: boolean }> => {
      if (!patientId) throw new Error('No patient id');
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('id', patientId)
        .single();
      if (error) throw error;
      return {
        ...(data as PatientRow),
        has_cpr: (data as PatientRow).encrypted_cpr !== null,
      };
    },
    staleTime: 10_000,
  });
}

export function useCreatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePatientInput): Promise<PatientMutationOutput> => {
      return await callFunction<CreatePatientInput, PatientMutationOutput>('create-patient', input);
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['patients', 'list', row.clinic_id] });
    },
  });
}

export function useUpdatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdatePatientInput): Promise<PatientMutationOutput> => {
      return await callFunction<UpdatePatientInput, PatientMutationOutput>('update-patient', input);
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['patients', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: patientDetailQueryKey(row.id) });
    },
  });
}

export function useArchivePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      patient_id: string;
      archive: boolean;
    }): Promise<PatientMutationOutput> => {
      return await callFunction<UpdatePatientInput, PatientMutationOutput>('update-patient', {
        patient_id: input.patient_id,
        status: input.archive ? 'archived' : 'active',
      });
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['patients', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: patientDetailQueryKey(row.id) });
    },
  });
}

export interface DecryptCprResult {
  cpr: string;
}

export function useDecryptCpr() {
  return useMutation({
    mutationFn: async (input: {
      patient_id: string;
      source: string;
      purpose?: string;
    }): Promise<DecryptCprResult> => {
      return await callFunction<typeof input, DecryptCprResult>('decrypt-cpr', input);
    },
  });
}

// Helper to satisfy unused-export linter on Json
export type { Json };
